// Registry i18n messages - Polish
// This file contains common messages shared across all registry components
// Module-specific messages (auth, logs, settings, user) are in their respective modules
// To use module translations, import and merge them in your project's i18n config
// Example: import { authPl } from '@/modules/auth/i18n'

export default {
  common: {
    welcome: 'Witaj',
    loading: 'Ładowanie...',
    error: 'Błąd',
    success: 'Sukces',
    cancel: 'Anuluj',
    save: 'Zapisz',
    create: 'Utwórz',
    add: 'Dodaj',
    remove: 'Usuń',
    delete: 'Usuń',
    edit: 'Edytuj',
    preview: 'Podgląd',
    close: 'Zamknij',
    confirm: 'Potwierdź',
    search: 'Szukaj',
    refresh: 'Odśwież',
    refreshed: 'Odświeżono',
    filter: 'Filtruj',
    sort: 'Sortuj',
    actions: 'Akcje',
    yes: 'Tak',
    no: 'Nie',
    previous: 'Poprzedni',
    next: 'Następny',
    columns: 'Kolumny',
    open_menu: 'Otwórz menu',
    back: 'Wstecz',
    done: 'Gotowe',
    never: 'Nigdy',
    or: 'lub',
    created: 'Utworzono',
    updated: 'Zaktualizowano',
    toggleSidebar: 'Przełącz pasek boczny',
    toggleDarkMode: 'Przełącz na tryb {mode}',
    darkMode: 'ciemny',
    lightMode: 'jasny',
    toggleLanguage: 'Przełącz język na {locale}',
    copyToClipboard: {
      success: 'Skopiowano do schowka',
      copied: 'Skopiowano',
      copy: 'Skopiuj',
    },
    pagination: {
      totalRows: 'Łącznie {total} wierszy',
      rowsPerPage: 'Wierszy na stronę',
      rowsPerPageValue: 'Wierszy na stronę: {count}',
      page: 'Strona',
      of: 'z',
      goToFirstPage: 'Przejdź do pierwszej strony',
      goToPreviousPage: 'Przejdź do poprzedniej strony',
      goToNextPage: 'Przejdź do następnej strony',
      goToLastPage: 'Przejdź do ostatniej strony',
    },
    pages: {
      landing: 'Strona główna',
      cookies: 'Cookies',
      privacy: 'Polityka prywatności',
      terms: 'Regulamin',
      contact: 'Kontakt',
      about: 'O aplikacji',
      aiContext: 'Kontekst AI',
      notFound: 'Strona nie znaleziona',
    },
  },
  markdown: {
    supported: 'Markdown jest wspierany',
    supportedHint: 'Możesz używać markdown do formatowania swojego tekstu. Obsługiwane funkcje: nagłówki, pogrubienie, kursywa, listy, linki, obrazy, bloki kodu, tabele, cytaty i więcej.',
    showingPreview: 'Pokazywanie podglądu',
    linkSecurity: {
      linkTooLong: 'Link jest zbyt długi i został zablokowany',
      dangerousProtocolBlocked: 'Niebezpieczny protokół został zablokowany',
    },
  },
  validation: {
    required: 'To pole jest wymagane',
    email: 'Nieprawidłowy adres email',
    min: 'Musi mieć co najmniej {min} znaków',
    min_length: 'Musi mieć co najmniej {min} znaków',
    max: 'Może mieć maksymalnie {max} znaków',
    password_mismatch: 'Hasła nie są identyczne',
    password_too_short: 'Hasło musi mieć co najmniej {min} znaków',
    invalid_token: 'Nieprawidłowy lub wygasły token',
  },
  errors: {
    generic: 'Wystąpił błąd. Spróbuj ponownie',
    validationError: 'Sprawdź błędy w formularzu',
    network: 'Błąd sieci. Sprawdź połączenie internetowe',
    unauthorized: 'Nie masz uprawnień do wykonania tej akcji',
    not_found: 'Zasób nie został znaleziony',
    server_error: 'Błąd serwera. Spróbuj ponownie później',
    chunkLoadError: {
      title: 'Nowa wersja aplikacji',
      message: 'Aplikacja została zaktualizowana. Aby kontynuować, należy odświeżyć stronę.',
      description: 'Zapisz swoją pracę przed odświeżeniem.',
      reload: 'Odśwież stronę',
      cancel: 'Anuluj',
    },
  },
  navigation: {
    main: 'Nawigacja',
    dashboard: 'Panel',
    profile: 'Profil',
    settings: 'Ustawienia',
  },
  footer: {
    cookies: 'Informacja o ciasteczkach',
    privacy: 'Polityka prywatności',
    terms: 'Regulamin',
    contact: 'Kontakt',
    github: 'GitHub',
    recaptcha: {
      notice: 'Ta strona jest chroniona przez reCAPTCHA.',
      privacy: 'Polityka prywatności',
      and: ' i ',
      terms: 'Warunki korzystania',
      google: ' Google mają zastosowanie.',
    },
  },
  cookies: {
    title: 'Informacja o ciasteczkach',
    subtitle: 'Informacje o wykorzystaniu danych w aplikacji',
    whatAre: {
      title: 'Czym są pliki cookie',
      description: 'Pliki cookie to małe pliki tekstowe, które są przechowywane na Twoim urządzeniu podczas przeglądania strony internetowej. Pomagają one aplikacji zapamiętać Twoje preferencje i zapewniają podstawowe funkcje.',
    },
    types: {
      title: 'Rodzaje plików cookie używanych przez nas',
      essential: {
        title: 'Niezbędne pliki cookie',
        description: 'Te pliki cookie są niezbędne do działania aplikacji i nie można ich wyłączyć. Używamy ich do:',
        items: {
          session: 'Utrzymania sesji użytkownika po zalogowaniu (token JWT)',
          security: 'Zapewnienia bezpieczeństwa (ochrona przed atakami CSRF)',
          preferences: 'Zapamiętania podstawowych preferencji (język, tryb ciemny)',
        },
      },
      functional: {
        title: 'Funkcjonalne pliki cookie',
        description: 'Te pliki cookie pomagają aplikacji lepiej funkcjonować i zapamiętywać Twoje wybory:',
        items: {
          settings: 'Ustawienia aplikacji przechowywane lokalnie (localStorage)',
          preferences: 'Preferencje wyświetlania i układu interfejsu',
        },
      },
    },
    localStorage: {
      title: 'LocalStorage',
      description: 'Oprócz plików cookie, aplikacja wykorzystuje również localStorage przeglądarki do przechowywania niektórych danych lokalnie na Twoim urządzeniu, takich jak:',
      items: {
        settings: 'Ustawienia aplikacji (preferencje językowe, jednostki wagowe)',
        cache: 'Tymczasowe dane cache do poprawy wydajności',
        offline: 'Dane offline do pracy bez połączenia z internetem',
      },
    },
    management: {
      title: 'Zarządzanie plikami cookie',
      description: 'Możesz zarządzać plikami cookie na kilka sposobów:',
      items: {
        browser: 'Za pomocą ustawień przeglądarki - większość przeglądarek pozwala na wyświetlanie i usuwanie plików cookie',
        block: 'Blokowanie niektórych typów plików cookie (może to wpłynąć na funkcjonalność aplikacji)',
        delete: 'Usuwanie plików cookie - możesz je usunąć w każdej chwili, jednak wymaga to ponownego logowania',
      },
      note: 'Uwaga: Wyłączenie niezbędnych plików cookie może spowodować, że niektóre funkcje aplikacji przestaną działać poprawnie.',
    },
    thirdParty: {
      title: 'Pliki cookie osób trzecich',
      description: 'Aplikacja może wykorzystywać usługi zewnętrzne (np. Google Analytics, reCAPTCHA), które mogą umieszczać własne pliki cookie. Zgodnie z naszą polityką prywatności, informujemy o wykorzystaniu takich usług i zapewniamy odpowiednie zabezpieczenia danych.',
    },
    privacy: {
      title: 'Prywatność',
      description: 'Wszystkie pliki cookie używane przez aplikację służą wyłącznie do zapewnienia funkcjonalności serwisu. Nie używamy plików cookie do śledzenia użytkowników w celach marketingowych ani do przekazywania danych osobowych osobom trzecim bez Twojej zgody.',
    },
    rodo: {
      title: 'RODO',
      description: 'Zgodnie z Rozporządzeniem Ogólnym o Ochronie Danych (RODO), niezbędne pliki cookie nie wymagają Twojej zgody, ponieważ są niezbędne do świadczenia usługi. W przypadku innych plików cookie będziemy prosić o Twoją zgodę, jeśli będzie to wymagane.',
    },
  },
  privacy: {
    title: 'Polityka prywatności',
    subtitle: 'Jak chronimy Twoje dane',
    dataCollection: {
      title: 'Zbieranie danych',
      description: 'Aplikacja CareerHub zbiera następujące dane:',
      items: {
        account: 'Dane konta: email, imię i nazwisko (wymagane do utworzenia konta)',
        gear: 'Dane profilu zawodowego: doświadczenie, projekty, umiejętności, wykształcenie, certyfikaty (przechowywane w bazie danych)',
        settings: 'Ustawienia aplikacji: preferencje językowe, motyw, inne ustawienia',
        usage: 'Dane użytkowania: logi dostępu, akcje podejmowane w aplikacji (w celu zapewnienia bezpieczeństwa i poprawy usługi)',
      },
    },
    dataStorage: {
      title: 'Przechowywanie danych',
      description: 'Wszystkie dane są przechowywane bezpiecznie na naszych serwerach w szyfrowanych bazach danych. Wykorzystujemy standardowe praktyki bezpieczeństwa, w tym hashowanie haseł, szyfrowanie połączeń (HTTPS) oraz regularne kopie zapasowe danych.',
    },
    dataAccess: {
      title: 'Dostęp do danych',
      description: 'Twoje dane są dostępne tylko dla Ciebie i są chronione hasłem. Nasi administratorzy mają dostęp techniczny do danych wyłącznie w celach utrzymania i poprawy usługi, zgodnie z najlepszymi praktykami bezpieczeństwa.',
      thirdParties: 'Nie udostępniamy Twoich danych osobowych osobom trzecim, z wyjątkiem przypadków wymaganych przez prawo lub z Twoją wyraźną zgodą.',
    },
    oauth: {
      title: 'Uwierzytelnianie OAuth',
      description: 'Oferujemy możliwość logowania się za pomocą zewnętrznych dostawców OAuth (Google, Facebook). Podczas korzystania z tej funkcji:',
      items: {
        receive: 'Otrzymujemy podstawowe informacje z Twojego konta (email, imię, zdjęcie profilowe)',
        store: 'Przechowujemy tylko niezbędne dane do świadczenia usługi',
        share: 'Nie udostępniamy Twoich danych z powrotem dostawcom OAuth bez Twojej zgody',
        revoke: 'Możesz w każdej chwili odwołać dostęp do swojego konta w ustawieniach dostawcy OAuth',
      },
      policies: 'Korzystając z OAuth, akceptujesz również politykę prywatności odpowiedniego dostawcy (Google lub Facebook).',
    },
    dataDeletion: {
      title: 'Usuwanie danych',
      description: 'Masz pełną kontrolę nad swoimi danymi i możesz w każdej chwili je usunąć. Aby usunąć konto i wszystkie powiązane dane:',
      steps: {
        settings: 'Przejdź do ustawień aplikacji',
        delete: 'Znajdź sekcję "Usuń konto"',
        confirm: 'Wpisz "DELETE" w polu potwierdzenia i potwierdź usunięcie',
      },
      alternative: 'Alternatywnie, możesz skontaktować się z nami pod adresem',
      request: 'i poprosić o usunięcie konta.',
      retention: 'Po usunięciu konta, Twoje dane są usuwane z naszych systemów w ciągu 30 dni. Niektóre dane mogą być przechowywane dłużej, jeśli jest to wymagane przez prawo lub w celu rozwiązania sporów.',
      oauth: 'Jeśli używasz logowania OAuth, musisz również odwołać dostęp do aplikacji w ustawieniach swojego konta u dostawcy OAuth (Google/Facebook).',
    },
    userRights: {
      title: 'Twoje prawa',
      description: 'Zgodnie z RODO, masz prawo do:',
      access: 'Dostępu do swoich danych osobowych',
      rectification: 'Sprawdzenia i poprawienia swoich danych',
      erasure: 'Usunięcia swoich danych ("prawo do bycia zapomnianym")',
      portability: 'Przeniesienia swoich danych do innej usługi',
      object: 'Sprzeciwu wobec przetwarzania danych',
      contact: 'Aby skorzystać z któregokolwiek z tych praw, skontaktuj się z nami pod adresem',
    },
    cookies: {
      title: 'Pliki cookie',
      description: 'Aplikacja wykorzystuje pliki cookie do utrzymania sesji użytkownika i poprawy funkcjonalności. Więcej informacji na temat wykorzystania plików cookie można znaleźć na stronie',
      link: 'Informacja o ciasteczkach',
    },
    changes: {
      title: 'Zmiany w polityce',
      description: 'W przypadku wprowadzenia istotnych zmian w sposobie przetwarzania danych, ta strona zostanie zaktualizowana, a użytkownicy zostaną poinformowani o zmianach za pośrednictwem emaila lub powiadomienia w aplikacji. Kontynuowanie korzystania z aplikacji po wprowadzeniu zmian oznacza akceptację zaktualizowanej polityki prywatności.',
    },
    contact: {
      title: 'Kontakt',
      description: 'W przypadku pytań dotyczących polityki prywatności lub przetwarzania danych, prosimy o kontakt:',
    },
  },
  terms: {
    title: 'Regulamin',
    subtitle: 'Warunki korzystania z serwisu',
    acceptance: {
      title: 'Akceptacja warunków',
      description: 'Korzystając z aplikacji CareerHub, akceptujesz niniejsze warunki korzystania. Jeśli nie zgadzasz się z którymkolwiek z poniższych warunków, nie powinieneś korzystać z serwisu.',
    },
    account: {
      title: 'Konto użytkownika',
      description: 'Aby korzystać z niektórych funkcji aplikacji, musisz utworzyć konto. Zobowiązujesz się do:',
      responsibilities: {
        provide: 'Podawania prawdziwych i aktualnych informacji podczas rejestracji',
        maintain: 'Utrzymywania bezpieczeństwa swojego konta i hasła',
        notify: 'Niezwłocznego powiadamiania nas o każdym nieuprawnionym użyciu Twojego konta',
        responsible: 'Odpowiedzialności za wszystkie działania podejmowane przy użyciu Twojego konta',
      },
    },
    oauth: {
      title: 'Uwierzytelnianie OAuth',
      description: 'Aplikacja umożliwia logowanie się za pomocą zewnętrznych dostawców OAuth (np. Google, Facebook). Korzystając z tej funkcji:',
      rules: {
        provider: 'Akceptujesz warunki użytkowania i politykę prywatności dostawcy OAuth',
        data: 'Wyrażasz zgodę na udostępnienie nam podstawowych informacji z Twojego konta (email, imię, zdjęcie profilowe)',
        revoke: 'Możesz w każdej chwili odwołać dostęp do swojego konta w ustawieniach dostawcy OAuth',
      },
    },
    content: {
      title: 'Zawartość użytkownika',
      description: 'Użytkownik zachowuje pełne prawa do treści, które tworzy i przechowuje w aplikacji. Użytkownik przyznaje nam jedynie niezbędną licencję do przechowywania, wyświetlania i przetwarzania tych danych w celu świadczenia usługi.',
    },
    prohibited: {
      title: 'Zakazane działania',
      description: 'Zabrania się:',
      items: {
        illegal: 'Używania aplikacji do celów niezgodnych z prawem',
        harmful: 'Przesyłania treści szkodliwych, obraźliwych lub niezgodnych z prawem',
        access: 'Próby nieuprawnionego dostępu do systemu lub innych kont użytkowników',
        disrupt: 'Zakłócania działania aplikacji lub serwerów',
        impersonate: 'Udawania innej osoby lub podawania się za kogoś innego',
      },
    },
    termination: {
      title: 'Zakończenie konta',
      description: 'Zastrzegamy sobie prawo do zawieszenia lub usunięcia Twojego konta w przypadku naruszenia niniejszych warunków lub z innych ważnych przyczyn. Możesz również w każdej chwili usunąć swoje konto w ustawieniach aplikacji.',
    },
    disclaimer: {
      title: 'Wyłączenie odpowiedzialności',
      description: 'Aplikacja jest świadczona "tak, jak jest", bez jakichkolwiek gwarancji. Nie ponosimy odpowiedzialności za jakiekolwiek szkody wynikające z korzystania z aplikacji.',
    },
    changes: {
      title: 'Zmiany w regulaminie',
      description: 'Zastrzegamy sobie prawo do wprowadzania zmian w niniejszym regulaminie. O istotnych zmianach poinformujemy użytkowników. Kontynuowanie korzystania z aplikacji po wprowadzeniu zmian oznacza akceptację nowych warunków.',
    },
    contact: {
      title: 'Kontakt',
      description: 'W przypadku pytań dotyczących niniejszego regulaminu, prosimy o kontakt za pomocą formularza kontaktowego dostępnego w aplikacji.',
    },
  },
  contact: {
    title: 'Kontakt',
    subtitle: 'Skontaktuj się z nami',
    info: {
      title: 'Informacje kontaktowe',
      description: 'Jeśli masz pytania, sugestie lub chcesz zgłosić problem, skontaktuj się z nami:',
    },
    email: {
      label: 'Email',
    },
    support: {
      title: 'Wsparcie',
      description: 'Staramy się odpowiadać na wszystkie wiadomości w ciągu 7 dni. Jeśli zgłaszasz problem techniczny, prosimy o dołączenie szczegółowych informacji o problemie.',
    },
  },
  landing: {
    title: 'CareerHub',
    subtitle: 'Jeden profil zawodowy. Tyle dopasowanych CV, ile potrzebujesz.',
    welcomeBack: 'Witaj ponownie, {name}!',
    feature1: {
      title: 'Zbuduj',
      description: 'Uzupełnij swoje doświadczenie, projekty i umiejętności raz',
    },
    feature2: {
      title: 'Dobierz',
      description: 'Wybierz to, co istotne dla danej aplikacji',
    },
    feature3: {
      title: 'Udostępnij',
      description: 'Wygeneruj dopasowane CV lub udostępnij link do swojego profilu',
    },
    login: 'Zaloguj się',
    register: 'Załóż konto',
    goToProfile: 'Przejdź do mojego profilu',
    footer: 'Zacznij budować swój profil już dziś',
  },
  notFound: {
    title: 'Strona nie została znaleziona',
    description: 'Strona, której szukasz, nie istnieje.',
    suggestions: 'Oto kilka pomocnych linków:',
    goToContainers: 'Przejdź do Kontenerów',
    goToDashboard: 'Przejdź do Dashboardu',
    goToSettings: 'Przejdź do Ustawień',
  },
  pwa: {
    update: {
      title: 'Nowa wersja dostępna',
      description: 'Dostępna jest nowa wersja aplikacji. Czy chcesz zaktualizować teraz?',
      reload: 'Zaktualizuj teraz',
      later: 'Później',
    },
  },
  fileUpload: {
    dropZone: {
      idle: 'Kliknij aby przesłać lub przeciągnij i upuść',
      dragging: 'Upuść pliki tutaj',
      disabled: 'Przesyłanie wyłączone',
    },
    formats: {
      image: 'JPEG, PNG, WEBP, GIF',
      any: 'Dowolny typ pliku',
    },
    limits: {
      maxSize: 'maks. {size} MB',
      maxFiles: '{count} plików | {count} plik | {count} pliki | {count} plików',
      maxFile: '{count} plików | {count} plik | {count} pliki | {count} plików',
    },
    errors: {
      tooManyFiles: 'Maksymalnie {max} plik(ów) dozwolone',
      fileTooLarge: '{name}: Rozmiar pliku przekracza {size} MB',
      invalidType: '{name}: Nieprawidłowy typ pliku',
    },
    imageGallery: {
      title: 'Zdjęcia',
      noImages: 'Nie przesłano jeszcze żadnych zdjęć',
      primary: 'Główne',
      tooltips: {
        setPrimary: 'Ustaw jako główne',
        dragToReorder: 'Przeciągnij aby zmienić kolejność',
        deleteImage: 'Usuń zdjęcie',
      },
      confirmDelete: 'Czy na pewno chcesz usunąć to zdjęcie?',
      messages: {
        loadFailed: 'Nie udało się wczytać zdjęć',
        uploadSuccess: 'Zdjęcie przesłane pomyślnie',
        uploadSuccessMultiple: '{count} zdjęć przesłanych pomyślnie',
        uploadFailed: 'Nie udało się przesłać zdjęcia',
        deleteSuccess: 'Zdjęcie usunięte pomyślnie',
        deleteFailed: 'Nie udało się usunąć zdjęcia',
        primarySuccess: 'Główne zdjęcie zaktualizowane',
        primaryFailed: 'Nie udało się ustawić głównego zdjęcia',
        reorderSuccess: 'Zmieniono kolejność zdjęć',
        reorderFailed: 'Nie udało się zmienić kolejności zdjęć',
      },
    },
  },
  about: {
    title: 'O aplikacji CareerHub',
    subtitle: 'Jeden profil zawodowy generujący dowolną liczbę dopasowanych CV',
    tableOfContents: {
      title: 'Spis treści',
    },
    overview: {
      title: 'Przegląd',
      description: 'CareerHub to aplikacja full-stack do budowania jednego, głównego profilu zawodowego - doświadczenia, projekty, umiejętności, wykształcenie, certyfikaty - i generowania z niego wielu dopasowanych CV. Łączy intuicyjny interfejs front-end z solidnym backendem, zapewniając synchronizację danych kariery między urządzeniami.',
    },
    capabilities: {
      title: 'Kluczowe możliwości',
      multiUser: 'Platforma wieloużytkownikowa - bezpieczne konta użytkowników z uwierzytelnianiem i autoryzacją',
      hybrid: 'Plany Freemium - Free, Pro i Expert ograniczają liczbę CV, znak wodny, funkcje AI i własną domenę',
      organization: 'Wyselekcjonowane CV - nazwane wersje CV wybierają, które doświadczenia, projekty, umiejętności i wykształcenie uwzględnić',
      metadata: 'Bogate metadane - śledzenie obowiązków, technologii, zanonimizowanych klientów, osiągnięć i certyfikatów',
      portability: 'Przenośność danych - import z LinkedIn, awaryjny import ze skopiowanego tekstu i pełny eksport JSON',
    },
    coreFeatures: {
      title: 'Główne funkcje',
      containers: {
        title: 'Profil',
        multiple: 'Wieloetapowy kreator profilu z automatycznym zapisem szkicu po każdym kroku',
        hierarchical: 'Wskaźnik kompletności zachęcający do uzupełnienia profilu',
        colors: 'Trzy poziomy widoczności - Prywatny, Znajomi lub Publiczny',
        metadata: 'Publiczna strona profilu pod unikalnym adresem, przyjazna SEO',
        cycle: 'Dane kontaktowe, nagłówek, podsumowanie i zdjęcie profilowe',
      },
      items: {
        title: 'Doświadczenie i projekty',
        rich: 'Bogate dane dla każdej roli: firma, stanowisko, typ zatrudnienia, daty, obowiązki, technologie',
        categorization: 'Projekty dokumentowane niezależnie od ról, następnie jawnie łączone z jednym lub kilkoma doświadczeniami i umiejętnościami',
        status: 'Anonimizacja - ukrycie prawdziwej nazwy firmy/klienta za placeholderem dla projektów objętych NDA',
        priority: 'Sekcje uporządkowane przez użytkownika metodą przeciągnij-i-upuść',
        expiration: 'Śledzenie osiągnięć, wyzwań i skali (wielkość zespołu, czas trwania, liczba użytkowników, budżet)',
      },
      analytics: {
        title: 'Umiejętności',
        weight: 'Kategoryzowane umiejętności (Techniczne/Narzędzia/Miękkie) z poziomem biegłości 1-5 i latami doświadczenia',
        readiness: 'Możliwość powiązania z konkretnymi projektami, aby pokazać gdzie umiejętność została zastosowana',
        charts: 'Sugestie umiejętności generowane przez AI dla wybranej roli (Pro/Expert)',
        statistics: 'Wspólny katalog technologii wykorzystywany między profilami',
      },
      search: {
        title: 'Generowanie CV',
        smart: 'Wiele nazwanych wersji CV na profil, każda jako wyselekcjonowany wybór z głównego profilu',
        filtering: 'Opcjonalne własne podsumowanie nadpisujące domyślne dla danej wersji CV',
        sorting: 'Wybór szablonu dla każdej wersji CV',
        expired: 'Asynchroniczne generowanie PDF jako zadanie w tle; PDF-y w planie Free mają znak wodny',
      },
      importExport: {
        title: 'Import/Eksport',
        json: 'Pełny eksport/import JSON danych profilu',
        markdown: 'Import z LinkedIn jako zadanie w tle',
        csv: 'Awaryjny parser tekstu wklejonego ręcznie, gdy eksport z LinkedIn jest niedostępny',
        crossDevice: 'Dostęp z wielu urządzeń - profil przechowywany po stronie serwera, nie tylko na jednym urządzeniu',
      },
    },
    businessFeatures: {
      title: 'Funkcje biznesowe',
      security: {
        title: 'Zarządzanie użytkownikami i bezpieczeństwo',
        auth: 'Rejestracja i logowanie użytkowników - uwierzytelnianie email/hasło z bezpiecznym hashowaniem haseł',
        oauth: 'Logowanie społecznościowe OAuth - logowanie przez Google, Facebook lub GitHub',
        email: 'Weryfikacja email - potwierdzanie adresów email dla bezpieczeństwa konta',
        '2fa': 'Uwierzytelnianie dwuskładnikowe (2FA) - TOTP (aplikacje autentykacyjne) i WebAuthn (klucze dostępu/klucze bezpieczeństwa)',
        password: 'Zarządzanie hasłami - resetowanie zapomnianych haseł, zmiana hasła dla uwierzytelnionych użytkowników',
        recaptcha: 'Ochrona reCAPTCHA v3 - niewidoczna ochrona przed botami przy logowaniu, rejestracji i resetowaniu hasła',
        session: 'Zarządzanie sesją - tokeny JWT z automatycznym odświeżaniem, bezpieczne wylogowanie',
        deletion: 'Usuwanie konta - zgodne z RODO miękkie usuwanie z potwierdzeniem',
      },
      profile: {
        title: 'Profil użytkownika',
        management: 'Zarządzanie profilem - aktualizacja nazwy, email i preferencji',
        avatar: 'Wsparcie awatarów - dostawcy OAuth automatycznie dostarczają zdjęcia profilowe',
        settings: 'Preferowane ustawienia - język, preferencje motywu',
        security: 'Ustawienia bezpieczeństwa - zarządzanie metodami 2FA, przeglądanie statusu bezpieczeństwa',
      },
      i18n: {
        title: 'Wsparcie wielojęzyczne',
        languages: 'Angielski i polski w pełni obsługiwane',
        detection: 'Automatyczne wykrywanie lokalizacji z przeglądarki',
        switching: 'Ręczne przełączanie języka w ustawieniach',
        localized: 'Cały tekst interfejsu, komunikaty walidacji i emaile zlokalizowane',
      },
      theming: {
        title: 'Motywowanie',
        dark: 'Tryb ciemny - pełne wsparcie ciemnego motywu z wykrywaniem preferencji systemowych',
        persistence: 'Trwałość motywu - ustawienia zapisywane na konto użytkownika',
      },
    },
    technical: {
      title: 'Stos technologiczny',
      frontend: {
        title: 'Frontend',
      },
      backend: {
        title: 'Backend',
      },
    },
  },
  aiContext: {
    title: 'Kontekst AI',
    subtitle: 'Krótki opis CareerHub w formacie Markdown dla asystentów AI takich jak ChatGPT',
    card: {
      title: 'Skopiuj kontekst do schowka',
      description: 'Kliknij przycisk poniżej, aby skopiować opis kontekstu. Następnie możesz wkleić go do ChatGPT lub innych asystentów AI, aby zapewnić kontekst o CareerHub.',
    },
    copied: 'Kontekst skopiowany do schowka',
  },
  premium: {
    premiumFeature: 'Funkcja Premium',
    unlockPremiumFeatures: 'Odblokuj Funkcje Premium - Uaktualnij, aby uzyskać dostęp do Asystenta AI i więcej',
  },
}
